import Icon from '@/components/icons/Icon';
import type { IconVariant } from '@/components/icons/types';
import { _t } from '@/locale';
import { useEffect, useState } from 'react';
import { ANY_EMPLOYEE_ID, getAnyEmployee } from '../constants';
import type { SelectEmployeeListItem } from '../views/SelectEmployeeList.types';
import SelectEmployeeCard, { radioSlot } from './SelectEmployeeCard';
import SelectEmployeeSheet from './SelectEmployeeSheet';

const baseKey = 'features.bookingSelection.SelectedEmployeePanel';

// A hard cap, unlike the calendar page's EmployeePicker, which renders up to seven in full:
// the panel's height must not depend on how many employees the place has.
const MAX_COLLAPSED_EMPLOYEES = 4;

type PanelRow = SelectEmployeeListItem & { avatarIcon?: IconVariant };

export type SelectedEmployeePanelProps = {
  heading: string;

  /** Bookable employees, already filtered + normalized. Excludes "Valfri utövare". */
  employees: SelectEmployeeListItem[];

  /** Currently selected employee id, or `ANY_EMPLOYEE_ID` for "Valfri utövare". */
  selectedEmployeeId?: number;

  /** Narrow: only the selected card renders; tapping it opens the "Välj utövare" drawer. */
  isNarrow?: boolean;

  /** Called with the picked employee's id, or `ANY_EMPLOYEE_ID` for "Valfri utövare". */
  onPick: (employeeId: number) => void;
};

/**
 * The "Vald utövare" panel. Wide is an inline picker; narrow collapses to the selected
 * card with the full list in a bottom-sheet drawer. Picking only calls `onPick`.
 */
const SelectedEmployeePanel = ({
  heading,
  employees,
  selectedEmployeeId,
  isNarrow = false,
  onPick,
}: SelectedEmployeePanelProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Widening closes the drawer, so narrowing back cannot re-open it unprompted.
  useEffect(() => {
    if (!isNarrow) setIsDrawerOpen(false);
  }, [isNarrow]);

  const anyEmployee = getAnyEmployee();
  const showAnyEmployee = employees.length > 1;
  // "Valfri utövare" is a choice, not a person: the icon tile stands in for an avatar.
  const anyEmployeeRow: PanelRow = {
    id: ANY_EMPLOYEE_ID,
    name: anyEmployee.about.name,
    jobTitle: anyEmployee.about.jobTitle,
    avatarIcon: 'users-alt',
  };

  const rows: PanelRow[] = showAnyEmployee ? [anyEmployeeRow, ...employees] : [...employees];
  const selectedRow = rows.find((row) => row.id === selectedEmployeeId);

  const collapsedRows: PanelRow[] = (() => {
    if (isNarrow) return selectedRow ? [selectedRow] : [];

    // A selection from past the cap takes the first slot instead of being appended, so the
    // count stays at MAX_COLLAPSED_EMPLOYEES. Looked up among `employees`, never `rows`:
    // the any-employee row is prepended below, so promoting it here would render it twice.
    const selectedEmployee = employees.find((employee) => employee.id === selectedEmployeeId);
    const ordered =
      selectedEmployee && employees.indexOf(selectedEmployee) >= MAX_COLLAPSED_EMPLOYEES
        ? [selectedEmployee, ...employees.filter((employee) => employee.id !== selectedEmployee.id)]
        : employees;

    const visible = ordered.slice(0, MAX_COLLAPSED_EMPLOYEES);
    return showAnyEmployee ? [anyEmployeeRow, ...visible] : visible;
  })();

  const canToggle = collapsedRows.length < rows.length;
  const visibleRows = isNarrow || !isExpanded ? collapsedRows : rows;

  return (
    <section aria-label={heading} className="gap-sm flex flex-col">
      <h2 className="text-fg_primary text-xl font-bold">{heading}</h2>
      <ul className="gap-sm flex flex-col">
        {visibleRows.map((row) => {
          const selected = row.id === selectedEmployeeId;
          return (
            <li key={row.id}>
              <SelectEmployeeCard
                name={row.name}
                jobTitle={row.jobTitle}
                avatarUrl={row.avatarUrl}
                avatarIcon={row.avatarIcon}
                rating={row.rating}
                selected={selected}
                onClick={isNarrow ? () => setIsDrawerOpen(true) : () => onPick(row.id)}
                rightSlot={
                  isNarrow ? <Icon variant="chevron-down" color="fg_primary" size="sm" /> : radioSlot(selected)
                }
              />
            </li>
          );
        })}
      </ul>
      {canToggle && !isNarrow && (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          className="py-xs text-m text-fg_link focus-visible:outline-border_focus flex w-full items-center justify-between rounded-sm outline-none focus-visible:outline focus-visible:outline-4">
          <span>{_t(isExpanded ? `${baseKey}.showFewer` : `${baseKey}.showMore`)}</span>
          <Icon variant={isExpanded ? 'chevron-up' : 'chevron-down'} color="fg_link" size="sm" />
        </button>
      )}
      {/* Prop-gated, not `{isNarrow && ...}`: leaving narrow closes the sheet instead of tearing it out. */}
      <SelectEmployeeSheet
        isOpen={isNarrow && isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        employeeList={{ employees, selectedEmployeeId, showAnyEmployee, onPick }}
      />
    </section>
  );
};

export default SelectedEmployeePanel;
